import { z } from 'zod'

const text = (max: number) => z.string().trim().max(max).default('')
export const serverInfoFieldsSchema = z
  .object({
    name: text(200),
    lobbyId: text(200),
    address: text(500),
    password: z.string().max(200).default(''),
    joinUrl: text(2000).refine((value) => {
      if (!value) return true
      try {
        const url = new URL(value)
        return (
          ['https:', 'http:'].includes(url.protocol) &&
          !url.username &&
          !url.password
        )
      } catch {
        return false
      }
    }, 'Enter a complete link starting with http:// or https://, without embedded credentials.'),
    instructions: text(4000),
  })
  .strict()
export const eventServerInfoSchema = z
  .object({
    fields: serverInfoFieldsSchema,
    releaseMode: z.enum(['immediate', 'before']).default('before'),
    offsetMinutes: z.coerce
      .number()
      .int()
      .min(0, 'Choose a release date and time no later than the event start.')
      .max(
        43200,
        'Choose a release date and time no more than 30 days before the event.',
      )
      .default(60),
    revision: z.number().int().min(0).default(0),
  })
  .strict()
  .refine((value) => !value.fields.password || hasServerInfo(value.fields), {
    message:
      'Add a server or lobby name, address, joining link or instructions alongside the password.',
    path: ['fields', 'name'],
  })
export type ServerInfoFields = z.infer<typeof serverInfoFieldsSchema>
export type EventServerInfo = z.infer<typeof eventServerInfoSchema>
export function hasServerInfo(fields: Partial<ServerInfoFields>) {
  return Boolean(
    fields.name?.trim() ||
    fields.lobbyId?.trim() ||
    fields.address?.trim() ||
    fields.joinUrl?.trim() ||
    fields.instructions?.trim(),
  )
}
export function defaultServerInfo(): EventServerInfo {
  return {
    fields: {
      name: '',
      lobbyId: '',
      address: '',
      password: '',
      joinUrl: '',
      instructions: '',
    },
    releaseMode: 'before',
    offsetMinutes: 60,
    revision: 0,
  }
}
export function serverInfoReleaseAt(
  config: { releaseMode: string; offsetMinutes: number },
  start: Date | null,
): Date | null {
  if (config.releaseMode === 'immediate') return null
  return start ? new Date(start.getTime() - config.offsetMinutes * 60000) : null
}
export function serverInfoPreset(gameName: string) {
  const f1 = /(^|[^a-z])f1|formula.?1/i.test(gameName)
  const iracing = /iracing/i.test(gameName)
  return {
    nameLabel: f1
      ? 'Lobby name'
      : iracing
        ? 'Hosted session name'
        : 'Server / lobby name',
    instructionsLabel: f1
      ? 'Host and invitation instructions'
      : 'Joining instructions',
    hint: f1
      ? 'Drivers must be invited by the host. Explain who will invite them and how to find the lobby.'
      : iracing
        ? 'Enter the hosted session name and password drivers use to join.'
        : 'Add the connection details drivers need for this game.',
    primaryPassword: !f1,
    primaryAddress: !f1 && !iracing,
  }
}
